import { useCallback } from 'react';
import type { Message, MessageV2 } from '@/hooks/useCommunication';
import { useCommunication } from '@/hooks/useCommunication';
import type { INavigator, ISerialPort } from '@/hooks/useSerialPort';
import { readReader, useSerialPort } from '@/hooks/useSerialPort';
import { BWS4GLoraEncoder } from '@/lib/protocol/encoder/bws-4g-lora';
import type { Bws4gLora } from '@/lib/protocol/parser/bws-4g-lora';
import { Bws4gLoraParser } from '@/lib/protocol/parser/bws-4g-lora';
import { isIccid, isImei, sleep, typedObjectEntries } from '@/lib/utils';
import { createValidationLog, validateConfiguration } from '@/lib/utils/configuration-validation';
import type {
  Bws4GLoraConfig,
  IConfigurationProfile,
} from '@/types/production/configuration-profile';
import type { Device } from '@/types/production/device';

namespace Namespace {
  export interface Equipment {
    firmware: string;
    serial: string;
    imei?: string | undefined;
    iccid?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }
  export interface Detected {
    port: ISerialPort;
    equipment: Equipment;
  }

  export type Profile = IConfigurationProfile<Bws4GLoraConfig>['config'];

  export type ConfigKeys =
    | keyof NonNullable<Profile['general']>
    | keyof NonNullable<Profile['specific']>
    | 'config_start'
    | 'config_stop';
}

// Autotest RS232: modo de loopback e texto enviado — o device deve devolver os mesmos bytes
const RS232_TEST_MODE = 4;
const RS232_LOOPBACK_PAYLOAD = 'texto';

// VCC do AUTOTEST vem em escalas diferentes conforme o firmware — a quantidade de dígitos
// define a escala e, com ela, a faixa aceita.
const VCC_RANGES_BY_DIGITS: Record<number, { min: number; max: number }> = {
  3: { min: 115, max: 130 },
  4: { min: 1150, max: 1300 },
};

const isVccInRange = (raw: string | undefined): boolean => {
  const digits = raw?.trim() ?? '';
  if (!/^\d+$/.test(digits)) return false;
  const range = VCC_RANGES_BY_DIGITS[digits.length];
  if (!range) return false;
  const value = Number(digits);
  return value >= range.min && value <= range.max;
};

const readResponse = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  command: string,
  timeout: number = 500,
  check?: string,
  unterminated = false,
): Promise<string | undefined> => {
  const decoder = new TextDecoder('utf-8', { fatal: false, ignoreBOM: true });
  let buffer = '';

  const base = command.replace('\r\n', '');
  const cmp = check ? check : base.includes('=') ? base : `${base}=`;

  // unterminated: a resposta não termina com \r\n (ex.: "[RS232] RAW | 74 65 ..."), então fica
  // presa no fim do buffer sem nunca virar linha completa. Ao fim do timeout, aceita esse trecho
  // se ele casar — opt-in para não devolver linha truncada nos demais comandos.
  const timeoutPromise = new Promise<string | undefined>((resolve) =>
    setTimeout(() => resolve(unterminated && buffer.includes(cmp) ? buffer : undefined), timeout),
  );

  const readPromise = (async () => {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Remove control chars (NULL, bell, backspace, etc.) — keep \r\n (0x0D=13, 0x0A=10)
      buffer = buffer.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

      console.info('[RAW DATA]', buffer);

      const lines = buffer.split('\r\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        // Agora compara usando cmp
        if (line.length > 0 && line.includes(cmp)) {
          return line;
        }
      }
    }
    return undefined;
  })();

  return Promise.race([readPromise, timeoutPromise]);
};

// BWS 4G aceita batch: "*N;CMD1;CMD2;...;CMDN" envia N comandos numa só transmissão.
// Recebe a lista de comandos JÁ com terminador (\r\n) — retorna a transmissão batch única.
const toBatchCommand = (commands: readonly string[]): string => {
  const stripped = commands.map((c) => c.replace(/\r?\n$/, '').trim()).filter((c) => c.length > 0);
  return `*${stripped.length};${stripped.join(';')}\r\n`;
};

const generateMessages = (profile: IConfigurationProfile): Record<Namespace.ConfigKeys, string> => {
  const response = {} as Record<Namespace.ConfigKeys, string>;
  Object.entries({
    ...profile.config.general,
    ...profile.config.specific,
  }).forEach(([command, args]) => {
    const _message = BWS4GLoraEncoder.encoder({ command, args } as any);
    if (!_message) return;
    response[command as Namespace.ConfigKeys] = _message;
  });
  return response;
};

export const useBWS4GLora = () => {
  const { ports, openPort, closePort, getReader, writeToPort, requestPort } = useSerialPort({});

  // Hook that handles communication process, like retries, delay between messages
  const { sendMultipleMessages, send } = useCommunication<ISerialPort>({
    openTransport: async (transport) => {
      // settleDelay 150ms (default 350): agiliza cada ciclo; o DF enviado antes do batch
      // de detecção absorve eventuais bytes perdidos logo após o open.
      await openPort(
        transport,
        {
          baudRate: 115200,
          stopBits: 1,
        },
        150,
      );
    },
    closeTransport: closePort,
    sendMessage: async (
      port,
      msg: Message<string, { check?: string; delay_before?: number; unterminated?: boolean }>,
    ) => {
      const reader = await getReader(port);
      if (!reader) throw new Error('Reader não disponível');
      const { command, timeout, delay_before, check, unterminated } = msg;
      if (delay_before) await sleep(delay_before);
      console.info('-------------------------');
      console.info('[MESSAGE SENT]', command);
      await writeToPort(port, command);
      const response = await readResponse(reader, command, timeout, check, unterminated);
      console.info('[RESPONSE MATCHED]', response);

      await reader.cancel();
      reader.releaseLock();
      return response;
    },
    sendMessageV2: async (
      port,
      msg: MessageV2<{
        delay_before?: number;
        delay_after?: number;
        check?: string;
      }>,
    ) => {
      const { command, delay_before, delay_after } = msg;
      if (delay_before) await sleep(delay_before);
      await writeToPort(port, command);
      console.log('[COMMAND]', command);
      if (delay_after) await sleep(delay_after);
    },
    options: {
      delayBetweenMessages: 400, // Default: stable for detection/identification
      maxRetriesPerMessage: 3,
      maxOverallRetries: 2,
    },
  });

  // Functions to interact with BWS4G via serial port
  const handleDetection = useCallback(
    async (ports: ISerialPort[]) => {
      // BWS 4G aceita batch: "*N;CMD1;CMD2;...;CMDN" envia N comandos numa só transmissão.
      // DF é enviado separado antes do batch: acorda o device e garante que o batch
      // seguinte (IMEI/ICCID/ET) responda de forma consistente.
      const messages = [{ command: 'DF\r\n' }, { command: '*3;IMEI;ICCID;ET\r\n' }] as const;
      return await Promise.all(
        ports.map(async (port) => {
          try {
            const { pos } = await send({
              transport: port,
              messages,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                // 10s timeout for stability
                const result = await readReader(reader, 10000);
                return result;
              },
            });
            const detection = pos
              ? Bws4gLoraParser.parser(pos, 'imei', 'iccid', 'firmware')
              : undefined;
            return {
              port,
              response: {
                imei: detection?.imei,
                iccid: detection?.iccid,
                firmware: detection?.firmware,
                serial: detection?.imei,
              },
            };
          } catch (error) {
            const name = (error as Error).name;
            const message = (error as Error).message ?? '';
            // Erros benignos e esperados durante a detecção — o device é lido de novo no
            // próximo intervalo (5s). Não poluem o console como erro:
            // - NetworkError / "Failed to open serial port": porta em uso ou desconectada.
            // - BreakError ("Break received"): o BWS 4G emite serial break ao entrar/sair do
            //   modo de teste (START/AUTOTEST rodando em paralelo à detecção) ou quando o USB
            //   reinicia. A leitura aberta da detecção recebe o break — é esperado, não é falha.
            const isBenign =
              name === 'NetworkError' ||
              name === 'BreakError' ||
              message.includes('Failed to open serial port') ||
              message.includes('Break received');
            if (isBenign) {
              // Silently skip - device will be detected again on next interval
              return { port };
            }
            console.error('[ERROR] handleDetection use-bws-4g-lora', error);
            return { port };
          }
        }),
      );
    },
    [getReader, send],
  );
  const handleGetConfig = useCallback(
    async (detected: Namespace.Detected[]) => {
      // Reads originais (preservados para o mapeamento de respostas).
      const messages = [
        { command: 'CHECK\r\n', key: 'check', delay_before: 1000 },
        { command: 'CXIP\r\n', key: 'cxip' },
        { command: 'STATUS\r\n', key: 'status' },
        { command: 'getignitiontype\r\n', key: 'ignition_type' },
        { command: 'getgpsperiodicwakeup\r\n', key: 'gps_periodic_wakeup' },
        { command: 'getalwaysgetagps\r\n', key: 'always_get_agps' },
        { command: 'getgpsfilterspeed\r\n', key: 'gps_filter_speed' },
        { command: 'GETNETWORKMODE\r\n', key: 'network_mode' },
        { command: 'getimpropermovement\r\n', key: 'improper_movement' },
      ] as const;

      // CXIP e GETNETWORKMODE dependem do modem (que pode resetar — "MDM SIM ERROR - RST MDM").
      // Quando enviados dentro do batch, a resposta do CXIP ("IP1=...,3000 IP2=...,3001") vem
      // numa linha avulsa que se perde no dump verboso/reset. Por isso ficam FORA do batch e
      // são enviados individualmente depois, com tempo para o modem responder.
      // Mesma estratégia de handleConfiguration.
      const MODEM_DEPENDENT_KEYS = new Set(['cxip', 'network_mode']);
      const batched_messages = messages.filter((m) => !MODEM_DEPENDENT_KEYS.has(m.key));
      const modem_dependent_messages = messages.filter((m) => MODEM_DEPENDENT_KEYS.has(m.key));

      // Envio em batch dos comandos que NÃO dependem do modem.
      const batch_messages = [
        {
          command: toBatchCommand(batched_messages.map((m) => m.command)),
          key: 'batch_read',
          delay_before: 1000,
        },
      ] as const;

      // Marcadores únicos de cada resposta do batch. Com early-exit, o readReader encerra
      // assim que TODOS aparecerem — antes do firmware emitir o dump verboso periódico
      // (--MODEM--, --GPS--) e antes de um eventual "MDM SIM ERROR - RST MDM", que poderiam
      // sobrescrever/atrasar a leitura. Os mesmos marcadores usados em handleConfiguration.
      const batchReadMarkers = [
        'Sim=', // CHECK
        'BATTERY EXTERNAL:', // STATUS
        'Ignition Type is', // getignitiontype
        'GPS Periodic Wakeup is', // getgpsperiodicwakeup
        'Always Get AGPS is', // getalwaysgetagps
        'GPS Speed Filter is', // getgpsfilterspeed
        'Improper Movement is', // getimpropermovement
      ];

      // Marcadores dos comandos do modem (enviados individualmente após o batch).
      const MODEM_CMD_MARKERS: Record<string, string> = {
        cxip: 'IP1=',
        network_mode: 'NetworkMode:',
      };

      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const { pos: batchPos } = await send({
              transport: port,
              messages: batch_messages as any,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                const result = await readReader(reader, 55000, 6000, batchReadMarkers);
                return result;
              },
            });

            // Envia CXIP e GETNETWORKMODE individualmente após o batch — esses comandos
            // perdem a resposta se enviados no batch enquanto o modem está em reset.
            const { pos: modemPos } = await send({
              transport: port,
              messages: modem_dependent_messages as any,
              delayBetweenMessages: 800,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                const markers = modem_dependent_messages
                  .map((m) => MODEM_CMD_MARKERS[m.key])
                  .filter((m): m is string => Boolean(m));
                const result = await readReader(reader, 20000, 3000, markers);
                return result;
              },
            });

            const pos = [...(batchPos ?? []), ...(modemPos ?? [])];

            console.info('[handleGetConfig POS RAW LINES]', pos ?? []);

            const raw_config = pos
              ? Bws4gLoraParser.parser(
                  pos,
                  'check',
                  'ip_primary',
                  'ip_secondary',
                  'status',
                  'ignition_type',
                  'gps_periodic_wakeup',
                  'always_get_agps',
                  'gps_filter_speed',
                  'improper_movement',
                )
              : {};

            // Process check response — parser already returns parsed object
            const {
              data_transmission_on,
              data_transmission_off,
              apn,
              keep_alive,
              ...processed_check
            } = raw_config.check ?? {};

            // ip — parser already returns parsed values
            // dns_primary/dns_secondary: CXIP response only contains IP1/IP2 — no DNS fields
            const ip_primary = raw_config.ip_primary;
            const ip_secondary = raw_config.ip_secondary;
            const dns_primary = undefined;
            const dns_secondary = undefined;

            // Process status response — parser already returns parsed object
            const horimeter = raw_config.status?.HR
              ? Bws4gLoraParser.horimeter(raw_config.status.HR)
              : undefined;
            const lock_type_variation = raw_config.status?.['OUT MODE']
              ? Bws4gLoraParser.lock_type_variation(raw_config.status['OUT MODE'])
              : undefined;

            // GET responses — parser already returns parsed values
            const ignition_type = raw_config.ignition_type;
            const gps_periodic_wakeup = raw_config.gps_periodic_wakeup;
            const always_get_agps = raw_config.always_get_agps;
            const gps_filter_speed = raw_config.gps_filter_speed;
            const improper_movement = raw_config.improper_movement;
            // network_mode: lido das linhas brutas (STATUS traz "NetworkMode: GSM+LTE" e o
            // GETNETWORKMODE traz o mesmo). O parser converte GSM+LTE→AUTO, LTE→4G, GSM→2G.
            const network_mode = pos
              ?.map((line) => Bws4gLoraParser.network_mode(line))
              .find((v) => v !== undefined);

            const config = {
              general: {
                data_transmission_on,
                data_transmission_off,
                ip_primary,
                ip_secondary,
                apn,
                keep_alive,
                dns_primary,
                dns_secondary,
              },
              specific: {
                ...processed_check,
                horimeter,
                lock_type_variation,
                ignition_type,
                gps_periodic_wakeup,
                always_get_agps,
                gps_filter_speed,
                network_mode,
                improper_movement,
              },
            };

            // Mapeia a chave do comando → valor lido para exibição na tabela de comandos.
            // Alguns comandos produzem chaves diferentes do próprio nome (cxip → ip_primary +
            // ip_secondary; network_mode é derivado das linhas brutas, não do raw_config).
            const responseByKey: Record<string, unknown> = {
              check: raw_config.check,
              status: raw_config.status,
              ignition_type,
              gps_periodic_wakeup,
              always_get_agps,
              gps_filter_speed,
              network_mode,
              improper_movement,
              cxip: ip_primary || ip_secondary ? { ip_primary, ip_secondary } : undefined,
            };

            return {
              port,
              equipment,
              config,
              messages: messages.map(({ key, command }) => ({
                key,
                request: command,
                response: responseByKey[key],
              })),
            };
          } catch (error) {
            console.error('[ERROR] handleGetConfig use-bws-4g-lora', error);
            return { port, equipment, messages: [], config: {} };
          }
        }),
      );
    },
    [getReader, send],
  );

  const handleConfiguration = useCallback(
    async (detected: Namespace.Detected[], configuration_profile: IConfigurationProfile) => {
      // Comandos que escrevem em flash/EEPROM precisam de mais tempo para persistir antes do próximo.
      const SLOW_WRITE_KEYS = [
        'odometer',
        'horimeter',
        'keep_alive',
        'data_transmission_on',
        'lock_type',
        'lock_type_variation',
        'network_mode',
      ];
      const write_messages_unsorted = typedObjectEntries(
        generateMessages(configuration_profile),
      ).map(([key, command]) => ({
        key: `w_${key}`,
        command,
        delay_after: SLOW_WRITE_KEYS.includes(key) ? 600 : 350,
      }));
      // network_mode (SETNETWORKMODE*) reinicia o dispositivo — deve ser enviado por último
      // para não interromper os demais comandos de escrita.
      // CONFIG_START vai no início para sinalizar ao firmware que entrou em modo de configuração;
      // o CONFIG_STOP correspondente é enviado ao final do fluxo (após reads e retries).
      const write_messages = [
        { key: 'w_config_start', command: 'CONFIG_START\r\n', delay_after: 350 },
        ...write_messages_unsorted.filter((m) => m.key !== 'w_network_mode'),
        ...write_messages_unsorted.filter((m) => m.key === 'w_network_mode'),
      ];

      const read_messages = [
        { command: 'CHECK\r\n', key: 'check', delay_before: 1000 },
        { command: 'CXIP\r\n', key: 'cxip' },
        { command: 'STATUS\r\n', key: 'status' },
        { command: 'getignitiontype\r\n', key: 'ignition_type' },
        { command: 'getgpsperiodicwakeup\r\n', key: 'gps_periodic_wakeup' },
        { command: 'getalwaysgetagps\r\n', key: 'always_get_agps' },
        { command: 'getgpsfilterspeed\r\n', key: 'gps_filter_speed' },
        { command: 'GETNETWORKMODE\r\n', key: 'network_mode' },
        { command: 'getimpropermovement\r\n', key: 'improper_movement' },
      ] as const;
      // CXIP e GETNETWORKMODE dependem do modem (que reseta no SETNETWORKMODE) — ficam FORA
      // do batch para evitar serem perdidos durante o RST MDM. São enviados individualmente
      // após o batch, com delay para o modem recuperar.
      const MODEM_DEPENDENT_KEYS = new Set(['cxip', 'network_mode']);
      const batched_read_messages = read_messages.filter((m) => !MODEM_DEPENDENT_KEYS.has(m.key));
      const modem_dependent_messages = read_messages.filter((m) => MODEM_DEPENDENT_KEYS.has(m.key));
      // Envio inicial dos reads em batch — retries permanecem individuais (granularidade preservada).
      const read_batch_messages = [
        {
          command: toBatchCommand(batched_read_messages.map((m) => m.command)),
          key: 'batch_read',
          delay_before: 1000,
        },
      ];

      // Se o perfil contém network_mode, o SETNETWORKMODE causa reinicialização do dispositivo.
      // Nesse caso o fluxo é dividido em duas fases: writes → aguarda reconexão → reads.
      const hasNetworkMode = write_messages.some((m) => m.key === 'w_network_mode');

      // Após SETNETWORKMODE, o device pode ou não reiniciar:
      // - Se reiniciar: SerialPort antigo fica inválido; OS reconecta com novo objeto via evento 'connect'.
      // - Se não reiniciar: a porta antiga continua válida.
      //
      // O listener 'connect' é registrado ANTES do send para não perder o evento.
      // O polling e closePort só ocorrem DEPOIS que o send terminar.
      // O listener permanece ativo até a Fase 2 terminar — cobre o caso onde o device
      // responde brevemente antes de reiniciar (resposta ao polling, depois reinicia).
      const listenForNewPort = (): {
        promise: Promise<ISerialPort>;
        cancel: () => void;
      } => {
        let resolve!: (port: ISerialPort) => void;
        const promise = new Promise<ISerialPort>((res) => {
          resolve = res;
        });
        const onConnect = (e: Event) => {
          const newPort = e.target as unknown as ISerialPort;
          if (!newPort) return;
          console.log('🔌 Evento connect — novo port object recebido');
          (navigator as INavigator).serial.removeEventListener('connect', onConnect);
          resolve(newPort);
        };
        (navigator as INavigator).serial.addEventListener('connect', onConnect);
        return {
          promise,
          cancel: () => (navigator as INavigator).serial.removeEventListener('connect', onConnect),
        };
      };

      // Aguarda um port válido após o send da Fase 1 terminar.
      // Recebe o listener já registrado e faz polling no port antigo em paralelo.
      // A Estratégia B aguarda 12s antes de começar — tempo para o device reiniciar se necessário.
      // Se o device reiniciou, o evento 'connect' chegará e vencerá antes do polling.
      const waitForReconnect = async (
        oldPort: ISerialPort,
        newPortListener: { promise: Promise<ISerialPort>; cancel: () => void },
        timeoutMs = 55000,
      ): Promise<ISerialPort> => {
        console.log('⏳ Aguardando device estabilizar após SETNETWORKMODE...');
        try {
          await closePort(oldPort);
        } catch {
          /* já fechada */
        }

        return new Promise((resolve, reject) => {
          let done = false;
          const finish = (p: ISerialPort, label: string) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            newPortListener.cancel();
            console.log(`✅ [${label}] Port disponível para Fase 2`);
            resolve(p);
          };

          const timer = setTimeout(() => {
            if (done) return;
            done = true;
            newPortListener.cancel();
            reject(new Error('Timeout aguardando reconexão do dispositivo'));
          }, timeoutMs);

          // Estratégia A: evento 'connect' — device reiniciou com novo objeto USB
          newPortListener.promise.then(async (newPort) => {
            if (done) return;
            try {
              await openPort(newPort, { baudRate: 115200, stopBits: 1 });
              const reader = await getReader(newPort);
              await readReader(reader, 4000, 1000);
              await reader.cancel();
              reader.releaseLock();
              await closePort(newPort);
            } catch {
              /* segue com o port mesmo assim */
            }
            finish(newPort, 'A');
          });
          // Estratégia B: polling no port antigo — device não reiniciou ou já voltou
          // Aguarda 12s antes de iniciar para dar tempo ao shutdown completo
          (async () => {
            await sleep(12000);
            while (!done) {
              await sleep(2000);
              if (done) break;
              try {
                await openPort(oldPort, { baudRate: 115200, stopBits: 1 });
                const reader = await getReader(oldPort);
                await readReader(reader, 2000, 500);
                await reader.cancel();
                reader.releaseLock();
                await closePort(oldPort);
                finish(oldPort, 'B');
                break;
              } catch {
                /* ainda não disponível */
              }
            }
          })();
        });
      };

      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const init_time = Date.now();
            console.group(`🔧 BWS4G_LORA CONFIGURATION - Serial: ${equipment.serial}`);
            console.log('⏱️ Configuration started at', new Date(init_time).toLocaleTimeString());
            console.log(
              `📋 Messages: ${write_messages.length} writes + ${read_messages.length} reads`,
            );

            const writeStart = Date.now();
            let pos: string[] | undefined;
            // activePort tracks the port to use for retries — may differ from port when SETNETWORKMODE causes reconnect
            let activePort: ISerialPort = port;

            if (hasNetworkMode) {
              // Listener registrado ANTES do send — não perde o evento de reconexão
              const newPortListener = listenForNewPort();

              // SETNETWORKMODE reinicia o device — o reader recebe BreakError quando o USB cai.
              // Isso é esperado: capturamos as linhas até o momento do reset e seguimos para a Fase 2.
              let writePos: string[] | undefined;
              try {
                const result = await send({
                  transport: port,
                  messages: write_messages as any,
                  delayBetweenMessages: 100,
                  preChunk: async (transport) => {
                    await getReader(transport);
                  },
                  posChunk: async (transport) => {
                    const reader = await getReader(transport);
                    try {
                      // Leitura curta: captura confirmações dos writes antes do device reiniciar
                      return await readReader(reader, 10000, 2000);
                    } catch (readError) {
                      const name = (readError as Error)?.name;
                      const message = (readError as Error)?.message ?? '';
                      if (name === 'BreakError' || message.includes('Break received')) {
                        console.info(
                          '🔁 BreakError no posChunk dos writes — device reiniciou (esperado)',
                        );
                        return [];
                      }
                      throw readError;
                    }
                  },
                });
                writePos = result.pos;
              } catch (sendError) {
                const name = (sendError as Error)?.name;
                const message = (sendError as Error)?.message ?? '';
                if (name === 'BreakError' || message.includes('Break received')) {
                  console.info(
                    '🔁 BreakError durante send dos writes — device reiniciou (esperado), seguindo para Fase 2',
                  );
                  writePos = [];
                } else {
                  throw sendError;
                }
              }

              // Send concluído — inicia espera pelo port disponível
              activePort = await waitForReconnect(port, newPortListener, 55000);

              // Fase 2: drena boot logs até o device ficar quieto, depois envia o batch CHECK.
              // O port vem FECHADO de waitForReconnect — precisamos abrir antes de pegar o reader.
              // Sem essa drenagem o firmware ainda está emitindo dumps periódicos e o batch CHECK
              // é descartado/sobrescrito.
              try {
                await openPort(activePort, { baudRate: 115200, stopBits: 1 });
                const drainReader = await getReader(activePort);
                try {
                  await readReader(drainReader, 15000, 2500); // até 15s, encerra após 2.5s de silêncio
                } catch (readError) {
                  const name = (readError as Error)?.name;
                  if (name !== 'BreakError') throw readError;
                  // BreakError aqui significa que o port ainda estava se estabilizando — segue.
                }
                // readReader pode já ter liberado o lock internamente (em caso de hard-timeout).
                // Cancel/release defensivos — ignoramos erro de "already released".
                try {
                  await drainReader.cancel();
                } catch {
                  /* já cancelado */
                }
                try {
                  drainReader.releaseLock();
                } catch {
                  /* já released */
                }
                await closePort(activePort);
              } catch (drainError) {
                console.warn('⚠️ Falha ao drenar boot logs (segue mesmo assim):', drainError);
                // Garante que o port não ficou aberto pendurado se a drenagem abortou
                try {
                  await closePort(activePort);
                } catch {
                  /* já fechado */
                }
              }
              // Marcadores únicos de cada resposta do batch — usados para early exit.
              // CXIP/NetworkMode foram removidos do batch (dependem do modem que pode estar
              // em reset) — são enviados individualmente após o batch.
              const batchReadMarkers = [
                'Sim=', // CHECK
                'BATTERY EXTERNAL:', // STATUS
                'Ignition Type is', // getignitiontype
                'GPS Periodic Wakeup is', // getgpsperiodicwakeup
                'Always Get AGPS is', // getalwaysgetagps
                'GPS Speed Filter is', // getgpsfilterspeed
                'Improper Movement is', // getimpropermovement
              ];
              const { pos: readPos } = await send({
                transport: activePort,
                messages: read_batch_messages as any,
                delayBetweenMessages: 400,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
                posChunk: async (transport) => {
                  const reader = await getReader(transport);
                  const result = await readReader(reader, 55000, 3000, batchReadMarkers);
                  return result;
                },
              });

              // Envia CXIP e GETNETWORKMODE individualmente após o batch — esses comandos
              // dependem do modem (que pode ter resetado por SETNETWORKMODE) e perdem a
              // resposta se enviados no batch enquanto o RST MDM está em curso.
              const MODEM_CMD_MARKERS: Record<string, string> = {
                cxip: 'IP1=',
                network_mode: 'NetworkMode:',
              };
              const { pos: modemPos } = await send({
                transport: activePort,
                messages: modem_dependent_messages as any,
                delayBetweenMessages: 800,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
                posChunk: async (transport) => {
                  const reader = await getReader(transport);
                  const markers = modem_dependent_messages
                    .map((m) => MODEM_CMD_MARKERS[m.key])
                    .filter((m): m is string => Boolean(m));
                  const result = await readReader(reader, 20000, 3000, markers);
                  return result;
                },
              });

              // Combina as linhas das três fases para validação
              pos = [...(writePos ?? []), ...(readPos ?? []), ...(modemPos ?? [])];
            } else {
              // Fluxo normal: writes individuais + reads em batch + reads do modem individuais.
              const batchReadMarkers = [
                'Sim=',
                'BATTERY EXTERNAL:',
                'Ignition Type is',
                'GPS Periodic Wakeup is',
                'Always Get AGPS is',
                'GPS Speed Filter is',
              ];
              const { pos: singlePos } = await send({
                transport: port,
                messages: [...write_messages, ...read_batch_messages] as any,
                delayBetweenMessages: 100,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
                posChunk: async (transport) => {
                  const reader = await getReader(transport);
                  const result = await readReader(reader, 55000, 3000, batchReadMarkers);
                  return result;
                },
              });

              // Envia CXIP e GETNETWORKMODE individualmente após o batch
              const MODEM_CMD_MARKERS: Record<string, string> = {
                cxip: 'IP1=',
                network_mode: 'NetworkMode:',
              };
              const { pos: modemPos } = await send({
                transport: port,
                messages: modem_dependent_messages as any,
                delayBetweenMessages: 800,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
                posChunk: async (transport) => {
                  const reader = await getReader(transport);
                  const markers = modem_dependent_messages
                    .map((m) => MODEM_CMD_MARKERS[m.key])
                    .filter((m): m is string => Boolean(m));
                  const result = await readReader(reader, 20000, 3000, markers);
                  return result;
                },
              });
              pos = [...(singlePos ?? []), ...(modemPos ?? [])];
            }

            const end_time = Date.now();
            const totalTime = end_time - init_time;
            const writeTime = end_time - writeStart;

            console.log(`✅ Initial commands completed in ${writeTime}ms`);
            console.log(
              `📊 Total configuration time: ${totalTime}ms (${(totalTime / 1000).toFixed(2)}s)`,
            );

            console.info('[POS RAW LINES]', pos ?? []);

            const raw_config = pos
              ? Bws4gLoraParser.parser(
                  pos,
                  'check',
                  'ip_primary',
                  'ip_secondary',
                  'status',
                  'ignition_type',
                  'gps_periodic_wakeup',
                  'always_get_agps',
                  'gps_filter_speed',
                  'improper_movement',
                )
              : {};

            const toParserKey = (key: string): keyof typeof raw_config => {
              if (key === 'cxip') return 'ip_primary';
              return key as keyof typeof raw_config;
            };

            const toRetryParserKeys = (key: string): Array<keyof typeof raw_config> => {
              if (key === 'cxip') return ['ip_primary', 'ip_secondary'];
              return [key as keyof typeof raw_config];
            };

            const maxRetries = 2;
            let totalRetryTime = 0;

            // Se o modem resetou durante o batch (MDM SIM ERROR / RST MDM), comandos que dependem
            // do modem (CXIP, GETNETWORKMODE) perdem suas respostas. Aguarda recovery antes do retry.
            const mdmResetDetected =
              pos?.some(
                (line) =>
                  typeof line === 'string' &&
                  (line.includes('MDM SIM ERROR') || line.includes('RST MDM')),
              ) ?? false;
            if (mdmResetDetected) {
              console.warn('⚠️ MDM reset detectado durante batch — aguardando 8s para recovery');
              await sleep(8000);
              // Invalida CHECK também: respostas podem ter sido truncadas pelo reset.
              // O loop de retry vai re-emitir os comandos cujo parser ficou vazio.
              if (raw_config.check && typeof raw_config.check === 'object') {
                const checkFields = Object.keys(raw_config.check);
                if (checkFields.length < 5) {
                  console.warn(
                    `⚠️ CHECK parcial (${checkFields.length} campos) — invalidando para re-leitura`,
                  );
                  raw_config.check = {};
                }
              }
            }

            const isEmptyResult = (key: keyof typeof raw_config): boolean => {
              const val = raw_config[key];
              if (val === undefined) return true;
              // check returns {} when the response arrived but no fields were parsed
              if (key === 'check' && typeof val === 'object' && Object.keys(val).length === 0)
                return true;
              return false;
            };

            for (let retry = 0; retry < maxRetries; retry++) {
              // Find read messages whose parsed result is still missing or empty
              const failedKeys = read_messages.filter((msg) => isEmptyResult(toParserKey(msg.key)));

              const cxipRelatedMissing = raw_config.ip_secondary === undefined;

              const needsCxipRetry =
                cxipRelatedMissing && !failedKeys.some((m) => m.key === 'cxip');
              const effectiveFailedKeys = needsCxipRetry
                ? [...failedKeys, read_messages.find((m) => m.key === 'cxip')!]
                : failedKeys;

              if (effectiveFailedKeys.length === 0) {
                console.log('✅ All READ commands successful - no retries needed');
                break;
              }

              const retryIterationStart = Date.now();
              console.info(
                `[RETRY #${retry + 1}] Retrying ${effectiveFailedKeys.length} failed commands:`,
                effectiveFailedKeys.map((f) => f.key),
              );

              // Mapa key → marcador único que identifica a resposta no buffer.
              const READ_KEY_TO_MARKER: Record<string, string> = {
                check: 'Sim=',
                cxip: 'IP1=',
                status: 'BATTERY EXTERNAL:',
                ignition_type: 'Ignition Type is',
                gps_periodic_wakeup: 'GPS Periodic Wakeup is',
                always_get_agps: 'Always Get AGPS is',
                gps_filter_speed: 'GPS Speed Filter is',
                network_mode: 'NetworkMode:',
                improper_movement: 'Improper Movement is',
              };
              const retryMarkers = effectiveFailedKeys
                .map((m) => READ_KEY_TO_MARKER[m.key])
                .filter((m): m is string => Boolean(m));

              // Retry only the failed commands (use activePort — may differ from port after SETNETWORKMODE reconnect)
              const { pos: retryPos } = await send({
                transport: activePort,
                messages: effectiveFailedKeys as any,
                delayBetweenMessages: 100,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
                posChunk: async (transport) => {
                  const reader = await getReader(transport);
                  const result = await readReader(reader, 55000, 6000, retryMarkers);
                  return result;
                },
              });

              const retryIterationTime = Date.now() - retryIterationStart;
              totalRetryTime += retryIterationTime;
              console.log(`⏱️ Retry #${retry + 1} completed in ${retryIterationTime}ms`);

              console.info(`[RETRY #${retry + 1} RAW LINES]`, retryPos ?? []);
              if (retryPos) {
                const retryParserKeys = [
                  ...new Set(effectiveFailedKeys.flatMap((f) => toRetryParserKeys(f.key))),
                ];
                const retry_response = Bws4gLoraParser.parser(retryPos, ...retryParserKeys);
                Object.assign(raw_config, retry_response);
              }
            }

            // Extract all values AFTER retries so stale values from failed initial parse don't slip through
            // input_1/input_2 são excluídos do CHECK — validados via resposta direta ("Input mode X is set!")
            const {
              data_transmission_on,
              data_transmission_off,
              apn,
              keep_alive,
              input_1: _check_input_1,
              input_2: _check_input_2,
              ...processed_check
            } = raw_config.check ?? {};

            const ip_primary = raw_config.ip_primary;
            const ip_secondary = raw_config.ip_secondary;
            // dns_primary/dns_secondary: CXIP response only contains IP1/IP2 — no DNS fields
            const dns_primary = undefined;
            const dns_secondary = undefined;

            const horimeter = raw_config.status?.HR
              ? Bws4gLoraParser.horimeter(raw_config.status.HR)
              : undefined;
            const lock_type_variation = raw_config.status?.['OUT MODE']
              ? Bws4gLoraParser.lock_type_variation(raw_config.status['OUT MODE'])
              : undefined;

            // Para campos cujo GET é não-determinístico, tentamos parsear a resposta direta do SET
            // nas linhas brutas. Se encontrar confirmação, usa o valor esperado como recebido.
            // Se não encontrar, o campo fica undefined e é removido da validação dinamicamente.
            const specific = configuration_profile.config.specific as any;

            // ignition_type: "Ignition Type is set to X" ou fallback do GET
            const ignition_type_from_pos = pos
              ?.map((line) => Bws4gLoraParser.ignition_type(line))
              .find((v) => v !== undefined);
            const ignition_type = ignition_type_from_pos ?? raw_config.ignition_type;

            // gps_periodic_wakeup: "GPS Periodic Wakeup is ON/OFF" pode vir como SET response
            const gps_periodic_wakeup_from_pos = pos
              ?.map((line) => Bws4gLoraParser.gps_periodic_wakeup(line))
              .find((v) => v !== undefined);
            const gps_periodic_wakeup =
              gps_periodic_wakeup_from_pos ?? raw_config.gps_periodic_wakeup;

            // always_get_agps: "Always Get AGPS is ON/OFF"
            const always_get_agps_from_pos = pos
              ?.map((line) => Bws4gLoraParser.always_get_agps(line))
              .find((v) => v !== undefined);
            const always_get_agps = always_get_agps_from_pos ?? raw_config.always_get_agps;

            // gps_filter_speed: "GPS Speed Filter is ON/OFF"
            const gps_filter_speed_from_pos = pos
              ?.map((line) => Bws4gLoraParser.gps_filter_speed(line))
              .find((v) => v !== undefined);
            const gps_filter_speed = gps_filter_speed_from_pos ?? raw_config.gps_filter_speed;

            // gps_replay_fix_sleep: "GPS Replay Fix Sleep is ON/OFF" — resposta do SET
            const gps_replay_fix_sleep_from_pos = pos
              ?.map((line) => Bws4gLoraParser.gps_replay_fix_sleep(line))
              .find((v) => v !== undefined);
            const gps_replay_fix_sleep = gps_replay_fix_sleep_from_pos;

            // improper_movement: "Improper Movement is ON/OFF"
            const improper_movement_from_pos = pos
              ?.map((line) => Bws4gLoraParser.improper_movement(line))
              .find((v) => v !== undefined);
            const improper_movement = improper_movement_from_pos ?? raw_config.improper_movement;

            // network_mode: prioriza a linha do STATUS (que reflete o modo CONFIGURADO),
            // pois GETNETWORKMODE pode retornar o modo ATIVO/REGISTRADO no momento (ex: "GSM"
            // mesmo quando configurado em AUTO se a rede atual está em 2G).
            const network_mode_from_status = pos
              ?.filter((line) => line.includes('BATTERY EXTERNAL') || line.includes('NetworkMode'))
              .find((line) => line.includes('NetworkMode'))
              ? Bws4gLoraParser.network_mode(
                  pos.find(
                    (line) => line.includes('BATTERY EXTERNAL') && line.includes('NetworkMode'),
                  ) ??
                    pos.find((line) => line.includes('NetworkMode:')) ??
                    '',
                )
              : undefined;
            const network_mode_from_get = pos
              ?.map((line) => Bws4gLoraParser.network_mode(line))
              .find((v) => v !== undefined);
            const network_mode = network_mode_from_status ?? network_mode_from_get;

            // input_1 / input_2: "Input mode X is set!" — se recebeu confirmação, aceita como correto
            const input_1 = pos?.some((line) => /Input mode 1 is set/i.test(line))
              ? specific?.input_1
              : undefined;
            const input_2 = pos?.some((line) => /Input mode 2 is set/i.test(line))
              ? specific?.input_2
              : undefined;

            // jammer_detection: o CHECK pode retornar JD=0 mesmo após JD1 ser aplicado porque
            // o firmware não persiste o valor imediatamente. Se encontrou confirmação direta do SET
            // ("JD1 is set!" / "JD0 is set!") no pos, usa o valor esperado do perfil como recebido.
            // Caso contrário, cai no valor lido pelo CHECK.
            const jammer_detection_confirmed = pos?.some((line) => /JD[01].*is set/i.test(line));
            const jammer_detection =
              jammer_detection_confirmed !== undefined && jammer_detection_confirmed
                ? specific?.jammer_detection
                : (processed_check as any)?.jammer_detection;

            // Build received config map for validation
            const expectedToReceivedMap: Record<string, unknown> = {
              data_transmission_on,
              data_transmission_off,
              ip_primary,
              ip_secondary,
              apn,
              keep_alive,
              dns_primary,
              dns_secondary,
              ...processed_check,
              horimeter,
              lock_type_variation,
              ignition_type,
              gps_periodic_wakeup,
              always_get_agps,
              gps_filter_speed,
              network_mode,
              input_1,
              input_2,
              jammer_detection,
              gps_replay_fix_sleep,
              improper_movement,
            };

            // Get expected config from profile
            const allExpectedConfig = {
              ...(configuration_profile.config.general ?? {}),
              ...(configuration_profile.config.specific ?? {}),
            } as Record<string, unknown>;

            // Fields sent as write commands that have no verifiable response.
            // Campos com undefined no mapa são adicionados dinamicamente para evitar falso negativo.
            const nonVerifiableFields = [
              'technology_system_name', // discriminator do schema — não é enviado ao dispositivo
              'protocol_type', // read-only — CHECK retorna PROT=E3+ mas não pode ser alterado
              'password', // segurança — não pode ser lido de volta do dispositivo
              'sms', // MG command — não é parseado no CHECK
              'timezone', // não enviado pelo BWS 4G — não validar
              'clear_buffer', // ação única — sem estado persistente
              'clear_horimeter', // ação única — sem estado persistente
              'horimeter', // valor INICIAL ≠ valor ACUMULADO retornado pelo STATUS
              'dns_primary', // CXIP response only contains IP1/IP2 — DNS fields are write-only
              'dns_secondary', // CXIP response only contains IP1/IP2 — DNS fields are write-only
              // Campos abaixo: verificados via resposta direta do SET no pos.
              // Se undefined, o dispositivo não confirmou — excluídos da validação.
              ...(network_mode === undefined ? ['network_mode'] : []),
              ...(ignition_type === undefined ? ['ignition_type'] : []),
              ...(gps_periodic_wakeup === undefined ? ['gps_periodic_wakeup'] : []),
              ...(gps_filter_speed === undefined ? ['gps_filter_speed'] : []),
              ...(always_get_agps === undefined ? ['always_get_agps'] : []),
              ...(input_1 === undefined ? ['input_1'] : []),
              ...(input_2 === undefined ? ['input_2'] : []),
              ...(jammer_detection === undefined ? ['jammer_detection'] : []),
              ...(gps_replay_fix_sleep === undefined ? ['gps_replay_fix_sleep'] : []),
              ...(improper_movement === undefined ? ['improper_movement'] : []),
              // lock_type / lock_type_variation: firmware atual não retorna resposta verificável.
              // Se enviado, considera correto — excluir sempre da validação.
              'lock_type',
              'lock_type_variation',
            ];

            const verifiableExpected = { ...allExpectedConfig };
            for (const field of nonVerifiableFields) {
              delete verifiableExpected[field];
            }

            // Validate configuration
            let validationResult = validateConfiguration(verifiableExpected, expectedToReceivedMap);

            // Check if all write commands received a response
            const writeCommandsSuccessful = write_messages.length > 0;

            // Retry divergent write commands up to 2 times
            // Fields that cannot be re-sent (network_mode causes reboot, password is write-only)
            const NON_RETRYABLE_WRITE_FIELDS = new Set([
              'network_mode',
              'password',
              'horimeter',
              'clear_buffer',
              'clear_horimeter',
              'sms',
            ]);

            const maxWriteRetries = 2;
            for (
              let writeRetry = 0;
              writeRetry < maxWriteRetries && !validationResult.isValid;
              writeRetry++
            ) {
              const divergentFields = validationResult.mismatches.map((m) => m.parameter);
              const retryableFields = divergentFields.filter(
                (f) => !NON_RETRYABLE_WRITE_FIELDS.has(f),
              );

              if (retryableFields.length === 0) break;

              console.warn(
                `[WRITE RETRY #${writeRetry + 1}] Reenviando ${retryableFields.length} comandos divergentes:`,
                retryableFields,
              );

              // Find the write messages that correspond to the divergent fields
              const retryWriteMessages = write_messages.filter((m) =>
                retryableFields.some((field) => m.key === `w_${field}`),
              );

              if (retryWriteMessages.length === 0) break;

              // Resend divergent write commands
              await send({
                transport: activePort,
                messages: retryWriteMessages as any,
                delayBetweenMessages: 500,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
              });

              // Wait for flash persistence then re-read CHECK (+ GET commands for fields not in CHECK)
              await sleep(3000);
              const needsStatusReread = retryableFields.includes('lock_type_variation');
              // always_get_agps, gps_filter_speed e improper_movement não estão no CHECK — precisam de GET próprio
              const needsAlwaysGetAgps = retryableFields.includes('always_get_agps');
              const needsGpsFilterSpeed = retryableFields.includes('gps_filter_speed');
              const needsImproperMovement = retryableFields.includes('improper_movement');
              const recheckMessages = [
                { command: 'CHECK\r\n', key: 'check', delay_before: 1000 },
                ...(needsStatusReread ? [{ command: 'STATUS\r\n', key: 'status' }] : []),
                ...(needsAlwaysGetAgps
                  ? [{ command: 'getalwaysgetagps\r\n', key: 'always_get_agps' }]
                  : []),
                ...(needsGpsFilterSpeed
                  ? [{ command: 'getgpsfilterspeed\r\n', key: 'gps_filter_speed' }]
                  : []),
                ...(needsImproperMovement
                  ? [{ command: 'getimpropermovement\r\n', key: 'improper_movement' }]
                  : []),
              ] as const;
              const recheckMarkers = [
                'Sim=',
                ...(needsStatusReread ? ['BATTERY EXTERNAL:'] : []),
                ...(needsAlwaysGetAgps ? ['Always Get AGPS is'] : []),
                ...(needsGpsFilterSpeed ? ['GPS Speed Filter is'] : []),
                ...(needsImproperMovement ? ['Improper Movement is'] : []),
              ];
              const { pos: recheckPos } = await send({
                transport: activePort,
                messages: recheckMessages as any,
                delayBetweenMessages: 400,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
                posChunk: async (transport) => {
                  const reader = await getReader(transport);
                  const result = await readReader(reader, 15000, 5000, recheckMarkers);
                  return result;
                },
              });

              console.info(`[WRITE RETRY #${writeRetry + 1} RAW LINES]`, recheckPos ?? []);

              if (recheckPos) {
                type ParserKey = keyof ReturnType<typeof Bws4gLoraParser.methods>;
                const parserKeys: ParserKey[] = [
                  'check',
                  ...(needsStatusReread ? ['status' as ParserKey] : []),
                  ...(needsAlwaysGetAgps ? ['always_get_agps' as ParserKey] : []),
                  ...(needsGpsFilterSpeed ? ['gps_filter_speed' as ParserKey] : []),
                  ...(needsImproperMovement ? ['improper_movement' as ParserKey] : []),
                ];
                const recheckConfig = Bws4gLoraParser.parser(recheckPos, ...parserKeys);
                if (recheckConfig.check && Object.keys(recheckConfig.check).length > 0) {
                  // Merge updated check values into raw_config and rebuild expectedToReceivedMap fields
                  Object.assign(raw_config, { check: recheckConfig.check });

                  const {
                    data_transmission_on: rDton,
                    data_transmission_off: rDtoff,
                    apn: rApn,
                    keep_alive: rKa,
                    input_1: _ri1,
                    input_2: _ri2,
                    ...rProcessedCheck
                  } = recheckConfig.check;

                  // Update only the fields that were retried
                  if (retryableFields.includes('data_transmission_on'))
                    expectedToReceivedMap.data_transmission_on = rDton;
                  if (retryableFields.includes('data_transmission_off'))
                    expectedToReceivedMap.data_transmission_off = rDtoff;
                  if (retryableFields.includes('apn')) expectedToReceivedMap.apn = rApn;
                  if (retryableFields.includes('keep_alive'))
                    expectedToReceivedMap.keep_alive = rKa;
                  for (const field of retryableFields) {
                    if (field in rProcessedCheck) {
                      expectedToReceivedMap[field] =
                        rProcessedCheck[field as keyof typeof rProcessedCheck];
                    }
                  }

                  // Pos accumulates all raw lines for write confirmation search
                  pos = [...(pos ?? []), ...recheckPos];
                }

                // Atualiza lock_type_variation a partir do STATUS recém-lido.
                if (needsStatusReread && recheckConfig.status?.['OUT MODE']) {
                  const newLtv = Bws4gLoraParser.lock_type_variation(
                    recheckConfig.status['OUT MODE'],
                  );
                  if (newLtv !== undefined) {
                    expectedToReceivedMap.lock_type_variation = newLtv;
                    Object.assign(raw_config, { status: recheckConfig.status });
                  }
                }

                // Atualiza always_get_agps, gps_filter_speed e improper_movement a partir dos GETs recém-lidos.
                if (needsAlwaysGetAgps && recheckConfig.always_get_agps !== undefined) {
                  expectedToReceivedMap.always_get_agps = recheckConfig.always_get_agps;
                }
                if (needsGpsFilterSpeed && recheckConfig.gps_filter_speed !== undefined) {
                  expectedToReceivedMap.gps_filter_speed = recheckConfig.gps_filter_speed;
                }
                if (needsImproperMovement && recheckConfig.improper_movement !== undefined) {
                  expectedToReceivedMap.improper_movement = recheckConfig.improper_movement;
                }
              }

              validationResult = validateConfiguration(verifiableExpected, expectedToReceivedMap);
              console.info(
                `[WRITE RETRY #${writeRetry + 1}] Resultado:`,
                validationResult.isValid
                  ? '✓ OK'
                  : `✗ Ainda divergente (${validationResult.mismatches.map((m) => m.parameter).join(', ')})`,
              );
            }

            // Log validation results
            if (writeCommandsSuccessful && !validationResult.isValid) {
              console.error('[CONFIG VALIDATION FAILED]');
              const logs = createValidationLog(validationResult);
              logs.forEach((log) => {
                console.error(`  ${log}`);
              });
            } else if (writeCommandsSuccessful && validationResult.isValid) {
              console.info('[CONFIG VALIDATION PASSED] ✓ Configuração OK');
            }

            // Final timing breakdown
            const finalTime = Date.now();
            const totalWithRetries = finalTime - init_time;

            console.table({
              'Write commands': `${writeTime}ms`,
              'Retry time': `${totalRetryTime}ms`,
              'Validation time': `${finalTime - end_time}ms`,
              TOTAL: `${totalWithRetries}ms (${(totalWithRetries / 1000).toFixed(2)}s)`,
            });
            console.log(
              `📊 Time per command avg: ${Math.round(totalWithRetries / (write_messages.length + read_messages.length))}ms`,
            );
            console.groupEnd();

            // Configuration is valid only if BOTH checks pass
            const status = writeCommandsSuccessful && validationResult.isValid;

            // For write commands (w_<field>), find the "is set" confirmation line in raw pos output.
            // The device responds with lines like "APN is set", "IP1 is set", "TX is set", etc.
            // Some fields map to a different device token than the field name itself.
            // For write commands, find the device's "is set" confirmation in the raw pos lines.
            // Strategy: extract the command token from the sent command string and search for
            // "<token>.*is set" in pos. This handles value-specific matches like "MODE30 is set!"
            // vs "MODE2 is set!" for lock_type vs lock_type_variation, avoiding false collisions.
            // For commands with non-obvious tokens (e.g. setignitiontype0 → "Ignition Type is set"),
            // a static override table is used.
            const WRITE_CONFIRMATION_OVERRIDES: Partial<Record<string, RegExp>> = {
              w_ignition_type: /Ignition Type.*is set/i,
              w_gps_filter_speed: /GPS Speed Filter.*is set/i,
              w_gps_periodic_wakeup: /GPS Periodic Wakeup.*is set/i,
              w_always_get_agps: /Always Get AGPS.*is set/i,
              w_network_mode: /setnetworkmode.*is set/i,
              w_input_1: /Input mode 1.*is set/i,
              w_input_2: /Input mode 2.*is set/i,
              w_gps_replay_fix_sleep: /GPS Replay Fix Sleep.*is set/i,
              w_improper_movement: /Improper Movement.*is set/i,
            };

            const findWriteConfirmation = (key: string, command: string): string | undefined => {
              if (!pos || !key.startsWith('w_')) return undefined;
              // Use static override when the device response token differs from the sent command
              const override = WRITE_CONFIRMATION_OVERRIDES[key];
              if (override) {
                return pos.find((l) => override.test(l))?.trim();
              }
              // Extract the command token (strip \r\n and any trailing separators like # or *)
              // then search for that exact token in a "... is set" confirmation line
              const token = command
                .replace(/\r\n$/, '')
                .replace(/[#*]\s*$/, '')
                .trim();
              if (token) {
                return pos.find((l) => new RegExp(`${token}.*is set`, 'i').test(l.trim()))?.trim();
              }
              return undefined;
            };

            // Maps read message key → parser key(s). Necessário porque alguns comandos
            // produzem chaves diferentes do próprio nome (ex: cxip → ip_primary + ip_secondary).
            const readKeyToParserKeys = (key: string): Array<keyof typeof raw_config> => {
              if (key === 'cxip') return ['ip_primary', 'ip_secondary'];
              return [key as keyof typeof raw_config];
            };

            // Build messages array with validation details
            const messages: Array<{
              key: string;
              request: string;
              response?: unknown;
            }> = [...write_messages, ...read_messages].map(({ key, command }) => {
              if (key.startsWith('w_')) {
                return { key, request: command, response: findWriteConfirmation(key, command) };
              }
              const parserKeys = readKeyToParserKeys(key);
              const pairs = parserKeys
                .map((pk) => [pk, raw_config[pk]] as const)
                .filter(([, v]) => v !== undefined);
              const response =
                pairs.length === 0
                  ? undefined
                  : pairs.length === 1
                    ? pairs[0][1]
                    : Object.fromEntries(pairs);
              return { key, request: command, response };
            });

            // Add validation result messages
            if (writeCommandsSuccessful && !validationResult.isValid) {
              messages.push({
                key: 'validation_result',
                request: 'CHECK',
                response: '✗ Configuração divergente',
              });
              // Add each mismatch as a separate message
              for (const mismatch of validationResult.mismatches) {
                messages.push({
                  key: `mismatch_${mismatch.parameter}`,
                  request: `CHECK ${mismatch.parameter}`,
                  response: `expected: ${JSON.stringify(mismatch.expected)}, received: ${JSON.stringify(mismatch.received)}`,
                });
              }
            } else if (writeCommandsSuccessful && validationResult.isValid) {
              messages.push({
                key: 'validation_result',
                request: 'CHECK',
                response: '✓ Configuração OK',
              });
            }

            // CONFIG_STOP encerra o modo de configuração no firmware. Best-effort:
            // se falhar (porta caiu, device em recovery), não invalida o resultado da config.
            try {
              await send({
                transport: activePort,
                messages: [{ key: 'w_config_stop', command: 'CONFIG_STOP\r\n' }] as any,
                delayBetweenMessages: 100,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
              });
              messages.push({
                key: 'w_config_stop',
                request: 'CONFIG_STOP\r\n',
                response: 'sent',
              });
            } catch (stopError) {
              console.warn('⚠️ Falha ao enviar CONFIG_STOP (segue mesmo assim):', stopError);
            }

            return {
              equipment,
              port,
              init_time,
              end_time,
              status,
              applied_profile: {
                general: {
                  data_transmission_on,
                  data_transmission_off,
                  ip_primary,
                  ip_secondary,
                  apn,
                  keep_alive,
                  dns_primary,
                  dns_secondary,
                },
                specific: {
                  ...processed_check,
                  horimeter,
                  lock_type_variation,
                  ignition_type,
                  gps_periodic_wakeup,
                  always_get_agps,
                  gps_filter_speed,
                  network_mode,
                },
              },
              messages,
            };
          } catch (error) {
            console.error('[ERROR] handleConfiguration use-bws-4g-lora', error);
            const message = (error as Error)?.message ?? '';
            const isCableDropped =
              message.includes('Writable stream not available') ||
              message.includes('Readable stream not available') ||
              message.includes('The port is closed') ||
              message.includes('The device has been lost');
            const error_reason = isCableDropped
              ? 'Cabo USB instável — verifique a conexão e tente novamente'
              : message || 'Erro desconhecido';
            return {
              port,
              status: false,
              equipment,
              messages: [{ key: 'error', request: '', response: error_reason }],
              init_time: 0,
              end_time: 0,
            };
          }
        }),
      );
    },
    [send, getReader, closePort, openPort],
  );
  const handleAutoTest = useCallback(
    async (ports: ISerialPort[]) => {
      return await Promise.all(
        ports.map(async (port) => {
          const resultTemplate = {
            port,
            response: {} as Record<string, Bws4gLora.AutoTest | string | undefined>,
            messages: [
              { key: 'start', command: 'START\r\n' },
              { key: 'autotest_1', command: 'AUTOTEST\r\n' },
              { key: 'autotest_2', command: 'AUTOTEST\r\n' },
              { key: 'autotest_3', command: 'AUTOTEST\r\n' },
              { key: 'autotest_4', command: 'AUTOTEST\r\n' },
              { key: 'autotest_5', command: 'AUTOTEST\r\n' },
              { key: 'lora_version', command: BWS4GLoraEncoder.lora_version() },
              { key: 'ble_version', command: BWS4GLoraEncoder.ble_version() },
              { key: 'rs232_mode', command: BWS4GLoraEncoder.rs232_mode(RS232_TEST_MODE) },
              { key: 'rs232', command: BWS4GLoraEncoder.rs232_send(RS232_LOOPBACK_PAYLOAD) },
            ],
            analysis: {} as Record<string, boolean>,
            // Dados do equipamento lidos durante o teste — mesclados ao equipment do log salvo
            equipment: {} as { lora_version?: string; ble_version?: string },
            init_time: Date.now(),
            end_time: 0,
            status: false,
          };

          try {
            // 1. Silencia o dump periódico de debug (--MODEM--, --GPS--) e envia START.
            // Sem o Debug OFF, as linhas de debug se intercalam com a resposta do AUTOTEST e
            // podem fazer o readResponse casar a linha errada ou estourar o timeout.
            // START recebe check/timeout explícitos porque o BWS 4G responde mais devagar
            // que o NB2 — o timeout default (500ms) do readResponse fecha a janela cedo demais.
            // Best-effort: são comandos de priming; se a confirmação não casar, seguimos para
            // o loop de AUTOTEST mesmo assim (o device já entrou em modo de teste ao receber START).
            try {
              const startResponse = await sendMultipleMessages({
                transport: port,
                messages: [
                  { key: 'debug_off', command: 'DF\r\n', check: 'Debug OFF', timeout: 3000 },
                  {
                    key: 'start',
                    command: 'START\r\n',
                    check: 'START',
                    timeout: 3000,
                    delay_before: 1000,
                  },
                ] as const,
              });
              resultTemplate.response.start = startResponse.start;
            } catch (startError) {
              console.warn(
                '[handleAutoTest bws-4g-lora] START/DF sem confirmação, seguindo:',
                startError,
              );
            }

            // 2. Configuração do loop de AUTOTEST
            const autotestTimeout = 25000; // 25s timeout total
            const startTime = Date.now();
            let remainingAttempts = 5;
            let lastBattResult = false;

            while (remainingAttempts > 0 && Date.now() - startTime < autotestTimeout) {
              const key = `autotest_${5 - remainingAttempts + 1}`;
              try {
                await sleep(2000); // Intervalo entre tentativas

                const autotestResponse = await sendMultipleMessages({
                  transport: port,
                  messages: [
                    {
                      key,
                      command: 'AUTOTEST\r\n',
                      // O BWS 4G responde "AUTOTEST=DEV:...,SN:...". Fixamos o marcador e um
                      // timeout amplo (4s) — o default (500ms) do readResponse é curto demais
                      // para o BWS 4G e fazia todas as 5 tentativas retornarem undefined,
                      // deixando analysis vazio (log descartado / status=false).
                      check: 'AUTOTEST=',
                      timeout: 4000,
                      transform: Bws4gLoraParser.auto_test,
                    },
                  ] as const,
                });

                const autotest = autotestResponse[key];

                if (!autotest) continue;

                const BATT_VOLT = Number(autotest.BATT_VOLT);
                const TEMP = Number(autotest.TEMP);

                const isBattGood = !Number.isNaN(BATT_VOLT) && BATT_VOLT <= 450 && BATT_VOLT >= 400;

                resultTemplate.analysis = {
                  // Firmware ainda é o mesmo do BWS 4G — aceita ambos até o 4G+LoRa ter DEV próprio
                  DEV: autotest.DEV === 'DM_BWS_PLUS_4G_LORA' || autotest.DEV === 'DM_BWS_4G',
                  ACELC: Boolean(autotest.ACELC?.length),
                  ACELP: autotest.ACELP === 'OK',
                  BATT_VOLT: lastBattResult || isBattGood,
                  CHARGER: autotest.CHARGER === 'OK',
                  FW: Boolean(autotest.FW?.length),
                  GPS: autotest.GPS === 'OK',
                  IC: isIccid(autotest.IC ?? ''),
                  ID_ACEL: Boolean(autotest.ID_ACEL?.length),
                  IM: isImei(autotest.IM ?? ''),
                  // Sem IN2: o 4G+LoRa só tem uma entrada e o AUTOTEST não reporta IN2
                  IN1: autotest.IN1 === 'OK',
                  MDM: autotest.MDM === 'OK',
                  OUT: autotest.OUT === 'OK',
                  RSI: autotest.RSI === 'OK',
                  SN: Boolean(autotest.SN?.length),
                  VCC: isVccInRange(autotest.VCC),
                  TEMP: !Number.isNaN(TEMP) && TEMP <= 28 && TEMP >= 23,
                };

                lastBattResult = resultTemplate.analysis.BATT_VOLT;

                const statusValues = Object.values(resultTemplate.analysis);

                resultTemplate.status =
                  statusValues.length > 0 && Object.values(resultTemplate.analysis).every(Boolean);

                resultTemplate.response[key] = autotest;

                if (resultTemplate.status) break;
              } catch (error) {
                console.warn(`Attempt ${5 - remainingAttempts + 1} failed`, error);
              }
              remainingAttempts--;
            }

            // 3. Testes específicos do 4G+LoRa (uma vez, ainda com debug OFF): versão dos
            // módulos LoRa e BLE e loopback da RS232 — envia um texto e confere se os bytes
            // recebidos (linha "RAW | 74 65 ...") voltam iguais. O check casa a linha de
            // resposta ("LORA VERSION=", sem colchetes) e não o log "[LORA] VERSION=", para
            // que a 2ª linha de um comando não sobre e case na leitura do comando seguinte.
            // Só roda se o AUTOTEST respondeu: com analysis vazio o useAutoTest descarta o
            // resultado como falha de comunicação — preencher os extras aqui faria a falha
            // ser gravada como reprovação do equipamento.
            if (Object.keys(resultTemplate.analysis).length > 0) {
              const autotestPassed = resultTemplate.status;
              let loraVersion: string | undefined;
              let bleVersion: string | undefined;
              let rs232Received: string | undefined;
              try {
                const extraResponse = await sendMultipleMessages({
                  transport: port,
                  messages: [
                    {
                      key: 'lora_version',
                      command: BWS4GLoraEncoder.lora_version(),
                      check: 'LORA VERSION=',
                      timeout: 3000,
                      transform: Bws4gLoraParser.lora_version,
                    },
                    {
                      key: 'ble_version',
                      command: BWS4GLoraEncoder.ble_version(),
                      check: 'BLE VERSION=',
                      timeout: 3000,
                      transform: Bws4gLoraParser.ble_version,
                    },
                    // O modo não é validado, mas a confirmação ("RS232 Mode is RS232-DEBUG-RAW")
                    // precisa ser consumida aqui: chega depois de 500ms e, sem check/timeout,
                    // sobrava para a leitura do rs232send, onde casava "RAW" por engano.
                    {
                      key: 'rs232_mode',
                      command: BWS4GLoraEncoder.rs232_mode(RS232_TEST_MODE),
                      check: 'RS232 Mode is',
                      timeout: 3000,
                    },
                    {
                      key: 'rs232',
                      command: BWS4GLoraEncoder.rs232_send(RS232_LOOPBACK_PAYLOAD),
                      // "RAW |" (com o pipe) não casa com o "RS232-DEBUG-RAW" da troca de modo.
                      // O firmware não termina a linha do RAW com \r\n — unterminated faz o
                      // readResponse aceitar o fim do buffer ao final do timeout.
                      check: 'RAW |',
                      timeout: 3000,
                      unterminated: true,
                      delay_before: 1500, // device precisa de ~1s após trocar o modo da RS232
                      transform: Bws4gLoraParser.rs232_raw,
                    },
                  ] as const,
                });
                loraVersion = extraResponse.lora_version;
                bleVersion = extraResponse.ble_version;
                rs232Received = extraResponse.rs232;
              } catch (extraError) {
                console.warn(
                  '[handleAutoTest bws-4g-lora] Falha nos testes LoRa/BLE/RS232:',
                  extraError,
                );
              }

              resultTemplate.response.lora_version = loraVersion;
              resultTemplate.response.ble_version = bleVersion;
              resultTemplate.response.rs232 = rs232Received;
              resultTemplate.equipment = { lora_version: loraVersion, ble_version: bleVersion };

              const extraAnalysis = {
                LORA_VERSION: Boolean(loraVersion),
                BLE_VERSION: Boolean(bleVersion),
                RS232: rs232Received === RS232_LOOPBACK_PAYLOAD,
              };
              resultTemplate.analysis = { ...resultTemplate.analysis, ...extraAnalysis };
              resultTemplate.status = autotestPassed && Object.values(extraAnalysis).every(Boolean);
            }

            // Reativa o debug ao final. check explícito: o device responde "Debug ON" —
            // sem ele o matcher derivado ("DN=") nunca casa e espera o timeout à toa.
            await sendMultipleMessages({
              transport: port,
              messages: [
                { key: 'debug_on', command: 'DN\r\n', check: 'Debug ON', timeout: 1500 },
              ] as const,
            });

            resultTemplate.end_time = Date.now();
            return resultTemplate;
          } catch (error) {
            console.error('[ERROR] handleAutoTest', error);
            resultTemplate.end_time = Date.now();
            return resultTemplate;
          }
        }),
      );
    },
    [sendMultipleMessages],
  );
  const handleIdentification = useCallback(
    async (detected: Namespace.Detected, rawId: string) => {
      const { port, equipment } = detected;
      // Remove whitespace acidental (tab/espaço de colagem) antes de qualquer uso.
      const id = rawId.replace(/\s+/g, '');

      try {
        const originalImei = equipment.imei || equipment.serial;
        console.info(
          `[BWS4G_LORA IDENTIFICATION] Starting identification: ${originalImei} → ${id}`,
        );

        const init_time = Date.now();
        const allMessages: Array<{
          key: string;
          request: string;
          response: string;
        }> = [];

        const writeMessages = [
          {
            key: 'write_imei',
            command: `13041SETSN,${id}\r\n`,
            check: 'BWS IMEI is set!',
            timeout: 2000,
          },
          {
            key: 'restart',
            command: 'RESTART\r\n',
            delay_before: 500,
          },
        ] as const;

        const writeResponse = await sendMultipleMessages({
          transport: port,
          messages: writeMessages,
        });

        allMessages.push(
          ...writeMessages.map(({ key, command }) => ({
            key,
            request: command,
            response: writeResponse[key] ?? '',
          })),
        );

        const writeSuccess = Boolean(
          writeResponse.write_imei?.includes('IMEI is set') ||
            writeResponse.write_imei?.includes('set'),
        );

        console.info(
          `[BWS4G_LORA IDENTIFICATION] Write command ${writeSuccess ? 'succeeded' : 'failed'}`,
        );

        try {
          await closePort(port);
        } catch (_e) {
          // Porta pode já estar fechada pelo comando RESTART
        }

        console.info('[BWS4G_LORA IDENTIFICATION] Waiting 40s for device restart...');
        await sleep(40000);

        let newImei: string | undefined;

        try {
          await openPort(port, {
            baudRate: 115200,
            stopBits: 1,
          });

          await sleep(2000);

          const readImeiMessages = [
            {
              command: 'IMEI\r\n',
              key: 'imei',
              check: 'IMEI=',
              delay_before: 1500,
            },
          ] as const;

          const readResponse = await sendMultipleMessages({
            transport: port,
            messages: readImeiMessages,
          });

          newImei = readResponse.imei ? Bws4gLoraParser.imei(readResponse.imei) : undefined;

          allMessages.push(
            ...readImeiMessages.map(({ key, command }) => ({
              key,
              request: command,
              response: readResponse[key] ?? '',
            })),
          );
        } catch (error) {
          // Sem a releitura não há como confirmar a gravação: newImei fica vazio e o
          // status reprova (antes assumia o id, aprovando uma gravação não confirmada).
          console.error('[BWS4G_LORA IDENTIFICATION] Error reading IMEI after restart:', error);
        } finally {
          try {
            await closePort(port);
          } catch {
            // Porta pode já estar fechada
          }
        }

        const end_time = Date.now();
        // Se o device não respondeu o IMEI de volta, não podemos confirmar a gravação.
        const actualImei = newImei ?? '';
        const imeiMatches = actualImei.length > 0 && actualImei === id;

        console.info(
          `[BWS4G_LORA IDENTIFICATION] Verification: expected ${id}, got ${actualImei || '(sem resposta)'} - ${imeiMatches ? 'OK' : 'MISMATCH'}`,
        );

        // Fallback do equipment_before: usa o id se o detected veio sem serial (estado anômalo
        // observado em produção quando a tabela mostra o serial mas o objeto detected.equipment está vazio).
        const beforeSerial = equipment.serial ?? equipment.imei ?? id;
        const beforeImei = equipment.imei ?? equipment.serial ?? id;
        // equipment_after sempre reporta o id desejado (que é o que ESTÁ no device se writeSuccess for true).
        const afterImei = actualImei.length > 0 ? actualImei : id;

        return {
          port,
          init_time,
          end_time,
          status: writeSuccess && imeiMatches,
          messages: allMessages,
          equipment_before: {
            serial: beforeSerial,
            imei: beforeImei,
          },
          equipment_after: {
            imei: afterImei,
            serial: afterImei,
          },
        };
      } catch (error) {
        const message = `[(${new Date().toLocaleString()}) ERROR IN BWS4G_LORA IDENTIFICATION]`;
        console.error(message, error);
        throw new Error(message);
      }
    },
    [sendMultipleMessages, closePort, openPort],
  );
  const handleGetIdentification = useCallback(
    async (port: ISerialPort) => {
      const messages = [
        {
          command: BWS4GLoraEncoder.imei(),
          key: 'imei',
          transform: Bws4gLoraParser.imei,
        },
      ] as const;
      try {
        const response = await sendMultipleMessages({
          transport: port,
          messages,
        });
        return { port, response: { ...response, serial: response.imei } };
      } catch (error) {
        console.error('[ERROR] handleGetIdentification e3-plus-4g', error);
        return { port };
      }
    },
    [sendMultipleMessages],
  );
  const handleFirmwareUpdate = useCallback(
    async (detected: Namespace.Detected[]) => {
      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const messages = [
              {
                key: 'update_firmware',
                command: `UPFW\r\n`,
              },
            ] as const;

            const init_time = Date.now();
            const response = await sendMultipleMessages({
              transport: port,
              messages,
            });
            const status = response.update_firmware && response.update_firmware.length > 0;

            const end_time = Date.now();
            return {
              port,
              init_time,
              end_time,
              status,
              messages: messages.map(({ key, command }) => ({
                key,
                request: command,
                response: response[key],
              })),
              equipment: {
                serial: equipment.serial,
                imei: equipment.imei,
                firmware: equipment.firmware,
              },
            };
          } catch (error) {
            const message = `[(${new Date().toLocaleString()}) ERROR IN BWS4G_LORA FIRMWARE UPDATE]`;
            console.error(message, error);
            throw new Error(message);
          }
        }),
      );
    },
    [sendMultipleMessages],
  );

  const isIdentified = (input?: {
    imei?: string;
    iccid?: string;
    firmware?: string;
  }): 'fully_identified' | 'partially_identified' | 'not_identified' => {
    if (!input) return 'not_identified';
    const { imei, iccid, firmware } = input;
    const identified = [imei, iccid, firmware];
    if (identified.every((e) => e && e?.length > 0)) {
      return 'fully_identified';
    } else if (identified.some((e) => e && e?.length > 0)) {
      return 'partially_identified';
    } else {
      return 'not_identified';
    }
  };

  return {
    isIdentified,
    ports,
    handleIdentification,
    handleGetConfig,
    handleConfiguration,
    requestPort,
    handleAutoTest,
    handleDetection,
    handleGetIdentification,
    handleFirmwareUpdate,
  };
};
